import {ConfEnv} from "../config/config";
import privateRequest from "../Infra/http/private.request";

export const MessageType = {
  NATIVE_PUSH_TOKEN: "NATIVE_PUSH_TOKEN",
  NOTIFICATION_RECEIVED: "NOTIFICATION_RECEIVED",
  NOTIFICATION_TAPPED: "NOTIFICATION_TAPPED",
  NATIVE_READY: "NATIVE_READY",

  REQUEST_PUSH_TOKEN: "REQUEST_PUSH_TOKEN",
  SUBSCRIBE_TO_TOPIC: "SUBSCRIBE_TO_TOPIC",
  UNSUBSCRIBE_FROM_TOPIC: "UNSUBSCRIBE_FROM_TOPIC",
  CLEAR_BADGE: "CLEAR_BADGE",
  WEB_READY: "WEB_READY",

  TOKEN_REGISTERED_SUCCESS: "TOKEN_REGISTERED_SUCCESS",
  TOKEN_REGISTERED_ERROR: "TOKEN_REGISTERED_ERROR",

  NAVIGATION_STATE_CHANGED: "NAVIGATION_STATE_CHANGED",
};

const API_ENDPOINTS = {
  REGISTER_TOKEN: `${ConfEnv.BASE_URL}/user/push-tokens`,
  DELETE_TOKEN: token => `${ConfEnv.BASE_URL}/user/push-tokens/${token}`,
  SUBSCRIBE_TOPIC: token => `${ConfEnv.BASE_URL}/user/push-tokens/${token}/topics`,
  UNSUBSCRIBE_TOPIC: (token, topic) => `${ConfEnv.BASE_URL}/user/push-tokens/${token}/topics/${topic}`,
};

let isNativeApp = false;
let nativePlatform = null;
let nativePushToken = null;
let pendingTokenRegistration = null;
let isNativeReady = false;
const messageListeners = new Map();

export const initializeNativeBridge = () => {
  if (typeof window === "undefined") return;

  isNativeApp = window.isNativeApp === true;
  nativePlatform = window.nativePlatform || null;

  if (isNativeApp) {
    document.documentElement.classList.add("native-app");
    if (nativePlatform) {
      document.documentElement.dataset.nativePlatform = nativePlatform;
    }
  }

  if (!isNativeApp) return;

  setupMessageListener();

  setTimeout(() => {
    sendToNative({type: MessageType.WEB_READY});
  }, 500);
};

const setupMessageListener = () => {
  if (typeof window === "undefined") return;

  window.addEventListener("nativeMessage", async event => {
    const message = event.detail;

    try {
      switch (message.type) {
        case MessageType.NATIVE_READY:
          isNativeReady = true;
          break;

        case MessageType.NATIVE_PUSH_TOKEN:
          nativePushToken = message.token;
          await handleNativePushToken(message);
          break;

        case MessageType.NOTIFICATION_RECEIVED:
          break;

        case MessageType.NOTIFICATION_TAPPED:
          await handleNotificationTapped(message);
          break;

        default:
          break;
      }

      const listeners = messageListeners.get(message.type) || [];
      for (const listener of listeners) {
        try {
          await listener(message);
        } catch {}
      }
    } catch {}
  });
};

const handleNativePushToken = async message => {
  const {token, platform} = message;

  if (!token) return;

  pendingTokenRegistration = {
    token,
    platform: platform || nativePlatform,
    deviceInfo: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: nativePlatform,
      timestamp: new Date().toISOString(),
    },
  };

  await attemptTokenRegistration();
};

const attemptTokenRegistration = async () => {
  if (!pendingTokenRegistration) return;

  try {
    await privateRequest.post(API_ENDPOINTS.REGISTER_TOKEN, pendingTokenRegistration);

    pendingTokenRegistration = null;

    sendToNative({
      type: MessageType.TOKEN_REGISTERED_SUCCESS,
    });

    return true;
  } catch (error) {
    if (error.response?.status !== 401) {
    }

    sendToNative({
      type: MessageType.TOKEN_REGISTERED_ERROR,
      error: error.message || "Unknown error",
    });

    return false;
  }
};

const handleNotificationTapped = async message => {
  const {notification} = message;

  if (!notification?.data) return;
};

export const sendToNative = message => {
  if (!isNativeApp) return false;

  if (typeof window === "undefined") return false;

  if (window.sendToNative) {
    window.sendToNative(message);
    return true;
  }

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    return true;
  }

  return false;
};

export const on = (messageType, callback) => {
  if (!messageListeners.has(messageType)) {
    messageListeners.set(messageType, []);
  }

  const listeners = messageListeners.get(messageType);
  listeners.push(callback);

  return () => {
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};

export const off = messageType => {
  messageListeners.delete(messageType);
};

export const isNative = () => isNativeApp;

/** True in WebView before initializeNativeBridge runs (injected flag or html class). */
export const isNativeShell = () => {
  if (typeof window === "undefined") return false;
  return window.isNativeApp === true || document.documentElement.classList.contains("native-app");
};

export const getPlatform = () => nativePlatform;

export const getPushToken = () => nativePushToken;

export const isReady = () => isNativeReady;

export const requestPushToken = () => {
  return sendToNative({
    type: MessageType.REQUEST_PUSH_TOKEN,
  });
};

export const subscribeToTopic = topic => {
  if (!topic) return false;

  return sendToNative({
    type: MessageType.SUBSCRIBE_TO_TOPIC,
    topic,
  });
};

export const unsubscribeFromTopic = topic => {
  if (!topic) return false;

  return sendToNative({
    type: MessageType.UNSUBSCRIBE_FROM_TOPIC,
    topic,
  });
};

export const clearBadge = () => {
  return sendToNative({
    type: MessageType.CLEAR_BADGE,
  });
};

// SPA route changes (pushState) don't update the native WebView's
// canGoBack on Android, so we report our own history position instead.
// This may be called before initializeNativeBridge runs, so it checks
// window.isNativeApp directly instead of the module flag.
export const sendNavigationState = canGoBack => {
  if (typeof window === "undefined" || window.isNativeApp !== true) return false;

  const message = {type: MessageType.NAVIGATION_STATE_CHANGED, canGoBack: canGoBack === true};

  if (window.sendToNative) {
    window.sendToNative(message);
    return true;
  }

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    return true;
  }

  return false;
};

export const retryTokenRegistration = async () => {
  return await attemptTokenRegistration();
};

export const deleteTokenFromBackend = async () => {
  if (!nativePushToken) return;
  await privateRequest.delete(API_ENDPOINTS.DELETE_TOKEN(nativePushToken));

  clearBadge();

  nativePushToken = null;
};

export const cleanup = () => {
  messageListeners.clear();
  nativePushToken = null;
  isNativeReady = false;
};

export default {
  initializeNativeBridge,
  sendToNative,
  on,
  off,
  isNative,
  isNativeShell,
  getPlatform,
  getPushToken,
  isReady,
  requestPushToken,
  subscribeToTopic,
  unsubscribeFromTopic,
  clearBadge,
  sendNavigationState,
  deleteTokenFromBackend,
  cleanup,
  MessageType,
};
